import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { TreeView } from "@mui/lab";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import RenderTreeView from "./RenderTreeView";
import TopButton from "../../../Components/UI/TopButtons";
import SecondaryModal from "../../../Components/Modal/SecondaryModal";
import Confirmation from "../../../Components/Modal/Confirmation";

import { findIndex, set } from "lodash";
import { getClassNames, getDefsByKeyId } from "../../../Js/Common";
import { createDefs, updateDefs, checkLearnerInClass } from "../Services";
import { userLogs } from "../../../Js/Common";

const ClassHierarchy = (props) => {
  // Hooks
  const classes = useStyles();
  const navigation = useNavigate();

  //State
  const [mode, setMode] = useState("view");
  const [enumData, setEnumData] = useState([]);
  const [enumList, setEnumList] = useState([]);

  const [hierarchyData, setHierarchyData] = useState(null);

  const [confirmModal, setConfirmModal] = useState(false);
  const [secondaryModal, setSecondaryModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  const [isUpdateEnum, setIsUpdateEnum] = useState(false);
  const [defaultEnumSelected, setDefaultEnumSelected] = useState([]);
  const [errorModal, setErrorModal] = useState(false);
  const [alertText, setAlertText] = useState(null);

  // Redux state
  const userInfo = useSelector((state) => state.loggedInUserInfo.userInfo);
  const products = useSelector((state) => state.defs.siteConfig.products);

  // React logic hooks
  useEffect(() => {
    if (mode) {
      fetchClassHierarchy();
      fetchEnumeration();
    }
  }, [mode]);

  // Fetch Class Hierarchy Data
  const fetchClassHierarchy = async () => {
    let resClassHierarchy = await getDefsByKeyId(
      "class_hierarchy",
      userInfo.site_uuid
    );
    // console.log("resClassHierarchy.data======>", resClassHierarchy.data);
    setHierarchyData(resClassHierarchy.data);
  };

  // Fetch enumeration Data
  const fetchEnumeration = async () => {
    let resEnumData = await getDefsByKeyId("enumeration", userInfo.site_uuid);
    setEnumData(resEnumData.data.keydata);
    if (resEnumData.data) {
      let enumListArr = resEnumData.data.keydata.map((keyDataObj) => {
        return {
          text: keyDataObj.name,
          value: keyDataObj.id,
        };
      });
      setEnumList(enumListArr);
      setDefaultEnumSelected(enumListArr[0]);
    }
  };

  const stringifyCircularJSON = (obj) => {
    const seen = new WeakSet();
    return JSON.stringify(obj, (k, v) => {
      if (v !== null && typeof v === "object") {
        if (seen.has(v)) return;
        seen.add(v);
      }
      return v;
    });
  };

  const addNodeBranch = (section, actionData) => {
    setIsUpdateEnum(false);
    let currentId = section.id;
    let selNodeObj = section;
    let nodenm = "";

    if (selNodeObj.child_enum === "") {
      nodenm = selNodeObj.children.length + 1;
    } else {
      let nodeEnumId = selNodeObj.child_enum;
      let selEnumObj = enumData.find(function (obj) {
        return obj.id === nodeEnumId;
      });
      let eData = selEnumObj.data;
      if (selNodeObj.children.length >= eData.length)
        nodenm = selNodeObj.children.length + 1;
      else nodenm = eData[selNodeObj.children.length];
    }
    addChild(section, currentId, nodenm, actionData);
  };

  const addChild = (section, currentId, nodedata, actionData) => {
    let isClass = false;
    let header = "child";

    if (actionData.value == "addcls") {
      isClass = true;
      header = "leaf";
    }

    if (section.children.length > 0) {
      isClass = section.children[0].isClass;
      header = section.children[0].header;
    }

    let arrCurrentIds = currentId.split("_");
    let rootElement = arrCurrentIds.slice(0, 3).join("_");
    let childElements = arrCurrentIds.slice(3, arrCurrentIds.length);

    let tmpClassHierarchyJson = hierarchyData;

    let objectPath = hierarchyData.keydata.children[0];
    let path = "";
    let idToSearch = rootElement;

    childElements.map((child, index) => {
      idToSearch += `_${child}`;
      let pos = findIndex(objectPath.children, { id: idToSearch });
      objectPath = objectPath.children[pos];
      if (index == 0) {
        path += `children[${pos}]`;
      } else {
        path += `.children[${pos}]`;
      }
    });

    if (typeof nodedata === "string" || typeof nodedata === "number") {
      let originNodeData = nodedata;
      nodedata = [];
      nodedata.push(originNodeData);
    }

    let btnid = "";
    for (let i = 0; i < nodedata.length; i++) {
      btnid = currentId + "_" + Math.random().toString(36).substr(7);
      objectPath.children.push({
        id: btnid,
        value: nodedata[i],
        children: [],
        header: header,
        isClass: isClass,
        child_enum: "",
      });
    }

    objectPath.enum = section.enum;
    objectPath.child_enum = section.child_enum;
    set(tmpClassHierarchyJson.keydata.children[0], path, objectPath);
    setHierarchyData(JSON.parse(stringifyCircularJSON(tmpClassHierarchyJson)));
    setIsUpdateEnum(false);
  };

  const createNode = (section, actionData) => {
    setIsUpdateEnum(false);
    let selNodeObj = section;
    if (selNodeObj.child_enum != "") {
      setAlertText(
        "Enumeration already made, to edit go to Edit Enumeration below"
      );
      setErrorModal(true);
      setTimeout(() => {
        resetState();
      }, 3000);
      return false;
    }
    let selEnum = enumList.find(function (obj) {
      return obj.value === selNodeObj.child_enum;
    });

    if (selEnum === undefined) {
      selEnum = enumList[0];
    }
    setDefaultEnumSelected(selEnum);
    setCurrentSection(section);
    setSelectedAction(actionData);
    setSecondaryModal(true);
  };

  const editNode = async (section, actionData) => {
    let selNodeObj = section;

    if (selNodeObj.children.length == 0) {
      setAlertText(
        "Class Enumeration not created, go to Set Enumeration to set names"
      );
      setErrorModal(true);
      setTimeout(() => {
        resetState();
      }, 3000);
      return false;
    }
    let selEnum = enumList.find(function (obj) {
      return obj.value === selNodeObj.child_enum;
    });
    setDefaultEnumSelected(selEnum);
    setCurrentSection(section);
    setSelectedAction(actionData);
    setSecondaryModal(true);
    setIsUpdateEnum(true);
  };

  const editTreeChild = (selectedEnum) => {
    // Add Enumeration List
    let selObj = enumData.find(function (obj) {
      return obj.id === selectedEnum.value;
    });

    let eData = selObj.data;
    let nodeChildData = currentSection.children;

    currentSection.enum = selectedEnum.text;
    currentSection.child_enum = selectedEnum.value;

    for (let i = 0; i < nodeChildData.length; i++) {
      if (i + 1 <= eData.length) nodeChildData[i].value = eData[i];
      else nodeChildData[i].value = i + 1;
    }
    resetState();
  };

  const addNode = (noOfIteration, selectedEnum) => {
    // Add Enumeration List
    let selObj = enumData.find(function (obj) {
      return obj.id === selectedEnum.value;
    });

    let eData = selObj.data;
    let currentId = currentSection.id;
    let nodenm = [];

    let obj = {
      ...currentSection,
      enum: selectedEnum.text,
      child_enum: selectedEnum.value,
    };
    setCurrentSection(obj);

    for (let i = 0; i < noOfIteration; i++) {
      if (i + 1 <= eData.length) nodenm.push(eData[i]);
      else nodenm.push(i + 1);
    }
    addChild(obj, currentId, nodenm, selectedAction);
    setSecondaryModal(false);
  };

  const removeNode = async (section, actionData) => {
    setIsUpdateEnum(false);
    if (section.children.length > 0) {
      setAlertText("Class has sections; cannot be removed ");
      setErrorModal(true);
      setTimeout(() => {
        resetState();
      }, 3000);
      return false;
    } else {
      if (section.isClass == true) {
        let response = await checkLearnerInClass(section.id);
        if (response.status == 200 && response.total > 0) {
          setAlertText(
            "You cannot remove as the learners are already added in this leaf."
          );
          setErrorModal(true);
          setTimeout(() => {
            resetState();
          }, 3000);
          return false;
        }
      }

      let currentId = section.id;
      let arrCurrentIds = currentId.split("_");
      let rootElement = arrCurrentIds.slice(0, 3).join("_");
      let childElements = arrCurrentIds.slice(3, arrCurrentIds.length - 1);

      let originClassHierarchyJson = hierarchyData;

      let objectPath = hierarchyData.keydata.children[0];
      let path = "";
      let idToSearch = rootElement;

      childElements.map((child, index) => {
        idToSearch += `_${child}`;
        let pos = findIndex(objectPath.children, { id: idToSearch });
        objectPath = objectPath.children[pos];
        if (index == 0) {
          path += `children[${pos}]`;
        } else {
          path += `.children[${pos}]`;
        }
      });

      let pos1 = findIndex(objectPath.children, { id: currentId });
      objectPath.children.splice(pos1, 1);

      if (objectPath.children.length == 0) {
        objectPath.child_enum = "";
        objectPath.enum = "";
      }
      set(originClassHierarchyJson.keydata.children[0], path, objectPath);
      setHierarchyData(
        JSON.parse(stringifyCircularJSON(originClassHierarchyJson))
      );
    }
  };

  const saveUpdatedTree = async () => {
    let postData = {
      keydata: hierarchyData.keydata,
    };
    let response = await updateDefs(
      postData,
      hierarchyData.uid,
      hierarchyData.site_uuid
    );

    if (response.status == 200) {
      // insert log for class hierarchy edit
      let res1 = {};
      res1.oldValue = "";
      res1.newValue = postData;
      let outcome = {};
      outcome.action = "edit";
      outcome.value = res1;

      await userLogs({
        actor: userInfo.uid,
        verb: "classHierarchy",
        target: hierarchyData.uid,
        outcome: outcome,
        site_uuid: userInfo.site_uuid,
      });

      let allClasses = await getClassNames(userInfo.site_uuid);
      await assignProduct(allClasses);
      setMode("view");
    }
  };

  // assignProduct
  const assignProduct = async (allClasses) => {
    let resProductToClass = await getDefsByKeyId(
      "product_to_class",
      userInfo.site_uuid
    );
    if (
      resProductToClass.data === undefined ||
      resProductToClass.data.length == 0
    ) {
      let arrProductToClass = [];
      for (let i = 0; i < products.length; i++) {
        let obj = {};
        obj.productId = products[i].productId;
        obj.productName = products[i].productName;
        obj.assignedTo = "all";
        obj.assignedClass = allClasses.map((classesData) => {
          return classesData.id;
        });
        arrProductToClass.push(obj);
      }
      let data = {};
      data.keyid = "product_to_class";
      data.keydata = arrProductToClass;
      data.site_uuid = userInfo.site_uuid;
      let params = {};
      await createDefs(data, params);
    } else {
      let productKeyData = resProductToClass.data.keydata;
      let arrProductToClass = [];
      for (let i = 0; i < products.length; i++) {
        let findObj = productKeyData.find((obj) => {
          return obj.productId == products[i].productId;
        });

        if (findObj == undefined) {
          let obj = {};
          obj.productId = products[i].productId;
          obj.productName = products[i].productName;
          obj.assignedTo = "all";
          obj.assignedClass = allClasses.map((classesData) => {
            return classesData.id;
          });
          arrProductToClass.push(obj);
        } else {
          if (findObj.assignedTo == "all") {
            findObj.assignedClass = allClasses.map((classesData) => {
              return classesData.id;
            });
          }
          arrProductToClass.push(findObj);
        }
      }
      let postData = {};
      postData.keyid = "product_to_class";
      postData.keydata = arrProductToClass;
      postData.is_synced = false;
      await updateDefs(
        postData,
        resProductToClass.data.uid,
        resProductToClass.data.site_uuid
      );
    }
  };

  const resetState = () => {
    setConfirmModal(false);
    setErrorModal(false);
    setAlertText(null);
    setSecondaryModal(false);
    setCurrentSection(null);
    setSelectedAction(null);
    setIsUpdateEnum(false);
  };

  return (
    <>
      <TopButton
        title={mode == "view" ? "Class Hierarchy" : "Update class hierarchy"}
        buttonText={mode == "view" ? "Edit" : "Update"}
        buttonType={mode == "view" ? "normal" : "success"}
        press={() => {
          mode == "view" ? setConfirmModal(!confirmModal) : saveUpdatedTree();
        }}
      ></TopButton>
      {hierarchyData && (
        <>
          <Box
            sx={{
              mt: 1,
              padding: 2,
              border: 1,
              borderColor: "#D9D9D9",
              borderRadius: 2,
            }}
          >
            <TreeView
              aria-label="file system navigator"
              className={[classes.root, classes.scrollBar]}
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              sx={{ height: "50vh", overflow: "auto", mx: 1, px: 5, ma: 2 }}
            >
              <RenderTreeView
                data={hierarchyData.keydata.children[0]}
                mode={mode}
                addNodeBranch={addNodeBranch}
                createNode={createNode}
                editNode={editNode}
                removeNode={removeNode}
              />
            </TreeView>
          </Box>
          <Button
            sx={{
              backgroundColor: "#5f5600ff",
              color: "#FFFFFF",
              textTransform: "inherit",
              borderRadius: 10,
              fontWeight: "600",
              mx: 1,
              mt: 1,
             '&:hover': { backgroundColor: '#EFEFEF', color: '#5f5600ff' },
            
              "@media (min-width: 1636px)": { fontSize: "1.2rem", px: 2 },
            }}
            onClick={() => {
              navigation("/CourseConfig");
            }}
          >
            Configure Course with School
          </Button>
        </>
      )}
      <Confirmation
        image="assign"
        primaryText="The school hierarchy is set."
        secondaryText="Do you want to edit it?"
        visible={confirmModal}
        positiveClick={() => {
          setConfirmModal(false);
          setMode("edit");
        }}
        negativeClick={() => {
          setConfirmModal(false);
        }}
        toggleModal={(flag) => {
          setConfirmModal(flag);
        }}
        persistent={true}
      ></Confirmation>
      <Confirmation
        image="error"
        primaryText={alertText}
        visible={errorModal}
        toggleModal={(flag) => setErrorModal(flag)}
        alert={true}
      ></Confirmation>
      <SecondaryModal
        title={"Select enumeration"}
        visible={secondaryModal}
        isUpdateEnum={isUpdateEnum}
        btnName={isUpdateEnum ? "Update" : "Save"}
        toggleModal={(flagVal) => {
          setSecondaryModal(flagVal);
          setIsUpdateEnum(false);
        }}
        options={enumList}
        defaultSelected={defaultEnumSelected}
        onPressSave={addNode}
        onPressUpdate={editTreeChild}
      ></SecondaryModal>
    </>
  );
};
const useStyles = makeStyles({
  scrollBar: {
    "&::-webkit-scrollbar": {
      width: "0.6em",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#ffe600ff",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
      borderRadius: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#5f5600ff",
      borderRadius: 5,
    },
  },
  "@global": {
    ".css-36wifs-MuiTreeItem-content.Mui-selected": {
      borderRadius: 50,
      border: 1,
    },
    ".css-36wifs-MuiTreeItem-content:hover": {
      borderRadius: 50,
      border: 1,
    },
  },
  root: {
    flexGrow: 1,
    color: "#DB8829",
  },
});
export default ClassHierarchy;
